"use client";

import { Dialog } from "@ark-ui/react/dialog";
import { Portal } from "@ark-ui/react/portal";
import { useCallback, useEffect, useRef, useState } from "react";

import { createWindowEventEmitter } from "@/utils/create-window-event-emitter";

import { backdropStyles, BaseModalProps, contentStyles, ownedOutsideHandlers } from "./shared";

export { ModalContainer, openModal };
export type { OpenModalArgs };

const [openModal, useModalListener] = createWindowEventEmitter<OpenModalArgs>("modal");

interface OpenModalArgs extends BaseModalProps {
  id: string;
}

function ModalContainer() {
  const [modalMap, setModalMap] = useState<Map<string, OpenModalArgs>>(new Map());

  const removeModal = useCallback((modalId: string) => {
    setModalMap((prev) => {
      const next = new Map(prev);
      next.delete(modalId);
      return next;
    });
  }, []);

  const addModal = useCallback((payload: OpenModalArgs) => {
    setModalMap((prev) => {
      const next = new Map(prev);
      next.set(payload.id, payload);
      return next;
    });
  }, []);

  useModalListener(addModal);

  if (!modalMap.size) return null;

  return (
    <>
      {Array.from(modalMap.values()).map(({ onExitComplete, ...modalProps }) => (
        <ImperativeModal
          key={modalProps.id}
          onExitComplete={() => {
            onExitComplete?.();
            removeModal(modalProps.id);
          }}
          {...modalProps}
        />
      ))}
    </>
  );
}

function ImperativeModal({
  renderContent,
  role = "dialog",
  dismissible = true,
  onExitComplete,
  id,
  ownsElement,
}: BaseModalProps) {
  // Hold a reference to the element that triggered the modal
  const lastFocusedElement = useRef(document.activeElement as HTMLElement | null);

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // This is a hack to workaround a bug in Ark UI.
    // If the Dialog is mounted with `open` set to `true`, the modal may not be interactive and
    // the exit animation will stop working.
    // Instead we'll initialize with the modal closed and immediately open it after the first render.
    // Reference: https://github.com/chakra-ui/ark/discussions/2295
    // oxlint-disable-next-line react/set-state-in-effect -- Ark UI dialogs mounted open lose interactivity; see the linked discussion above
    setIsOpen(true);
  }, []);

  // Stable for the same reason as in `Modal`; see the comment there.
  const close = useCallback(() => setIsOpen(false), []);

  return (
    <Dialog.Root
      modal
      restoreFocus
      finalFocusEl={() => lastFocusedElement.current}
      role={role}
      open={isOpen}
      onOpenChange={(e) => setIsOpen(e.open)}
      closeOnEscape={dismissible}
      closeOnInteractOutside={dismissible}
      {...ownedOutsideHandlers(ownsElement)}
      onExitComplete={onExitComplete}
    >
      <Portal>
        <Dialog.Backdrop className={backdropStyles} />
        <Dialog.Positioner>
          <Dialog.Content className={contentStyles} data-modal-id={id}>
            {renderContent({ close, isOpen })}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
